import { useAuth } from '../contexts/AuthContext';

/** Listas para rutas/menú: acceso al módulo con cualquier permiso (ver, agregar, editar, eliminar o legacy manage). */
export const ACCESS_PROPERTIES = [
  'view_properties',
  'manage_properties',
  'create_properties',
  'update_properties',
  'delete_properties',
] as const;

export const ACCESS_RENTALS = [
  'view_rentals',
  'manage_rentals',
  'create_rentals',
  'update_rentals',
  'delete_rentals',
] as const;

export const ACCESS_APPLICATIONS = [
  'view_applications',
  'manage_applications',
  'create_applications',
  'update_applications',
  'delete_applications',
] as const;

// Submódulos del menú Rentas (para controlar acceso por sección)
export const ACCESS_PROSPECTS = ['view_prospects'] as const;
export const ACCESS_TENANTS = ['view_tenants'] as const;
export const ACCESS_RENT_PAYMENTS = ['view_rent_payments'] as const;

/** Menú padre «Rentas»: visible si puede ver al menos una subsección. */
export const ACCESS_RENTALS_MENU = [
  ...ACCESS_APPLICATIONS,
  ...ACCESS_PROSPECTS,
  ...ACCESS_TENANTS,
  ...ACCESS_RENT_PAYMENTS,
] as const;

export const ACCESS_INVESTORS = [
  'view_investors',
  'manage_investors',
  'create_investors',
  'update_investors',
  'delete_investors',
] as const;

export const ACCESS_USERS = [
  'view_users',
  'manage_users',
  'create_users',
  'update_users',
  'delete_users',
] as const;

export const ACCESS_PROPERTY_MANAGEMENT = [
  'view_property_management',
  'manage_property_management',
  'create_property_management',
  'update_property_management',
  'delete_property_management',
] as const;

export const ACCESS_MAINTENANCE_REPORTS = [
  'view_maintenance_reports',
  'manage_maintenance_reports',
  'create_maintenance_reports',
  'update_maintenance_reports',
  'delete_maintenance_reports',
] as const;

/** Menú padre «Estadísticas»: visible si puede ver al menos una subsección. */
export const ACCESS_STATISTICS_MENU = [
  'view_statistics',
  'view_statistics_dashboard',
  'view_statistics_occupancy',
  'view_statistics_revenue',
  'view_statistics_price_m2',
  'view_statistics_units',
] as const;

export const ACCESS_STATS_DASHBOARD = ['view_statistics', 'view_statistics_dashboard'] as const;
export const ACCESS_STATS_OCCUPANCY = ['view_statistics', 'view_statistics_occupancy'] as const;
export const ACCESS_STATS_REVENUE = ['view_statistics', 'view_statistics_revenue'] as const;
export const ACCESS_STATS_PRICE_M2 = ['view_statistics', 'view_statistics_price_m2'] as const;
/** Unidades dentro de «Estadísticas»: solo permisos de estadísticas (no se hereda de Propiedades). */
export const ACCESS_STATS_UNITS = ['view_statistics', 'view_statistics_units'] as const;

/**
 * Hook para verificar permisos del usuario actual
 */
export const usePermissions = () => {
  const { user } = useAuth();

  const permissions = user?.role?.permissions || [];

  /**
   * Verifica si el usuario tiene un permiso específico
   */
  const hasPermission = (permission: string): boolean => {
    if (!user) return false;

    // Si el usuario es admin (nombre en BD o display), tiene todos los permisos
    if (user.role.name === 'Administrador' || user.role.name === 'admin' || permissions.includes('all')) {
      return true;
    }

    return permissions.includes(permission);
  };

  /**
   * Verifica si el usuario tiene al menos uno de los permisos especificados
   */
  const hasAnyPermission = (permissionList: string[]): boolean => {
    return permissionList.some((permission) => hasPermission(permission));
  };

  /**
   * Verifica si el usuario tiene todos los permisos especificados
   */
  const hasAllPermissions = (permissionList: string[]): boolean => {
    return permissionList.every((permission) => hasPermission(permission));
  };

  /**
   * Legacy: rol con `manage_*` aún implica poder agregar y editar en compatibilidad.
   */
  const canManage = (resource: string): boolean => {
    return hasPermission(`manage_${resource}`);
  };

  const canCreate = (resource: string): boolean => {
    return hasPermission(`create_${resource}`) || hasPermission(`manage_${resource}`);
  };

  const canUpdate = (resource: string): boolean => {
    return hasPermission(`update_${resource}`) || hasPermission(`manage_${resource}`);
  };

  /**
   * Ver si listados/páginas del módulo (view_* o cualquier acción sobre el recurso).
   * `resource` usa el sufijo sin prefijo: properties, rentals, applications, investors, users, property_management, maintenance_reports
   */
  const canView = (resource: string): boolean => {
    return (
      hasPermission(`view_${resource}`) ||
      hasPermission(`manage_${resource}`) ||
      hasPermission(`create_${resource}`) ||
      hasPermission(`update_${resource}`) ||
      hasPermission(`delete_${resource}`)
    );
  };

  return {
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canManage,
    canCreate,
    canUpdate,
    canView,
    isAdmin:
      user?.role?.name === 'Administrador' || user?.role?.name === 'admin' || permissions.includes('all'),
  };
};
